import authHeader from "@/services/auth/AuthHeader";
import axios from "axios";

/**
 * VIVA ISV onboarding for the logged-in merchant. Goes through CagistaServices `/api/site/viva/**`
 * (ADMIN, tenant-isolated). `locationId` is the location document id. Config-gated server-side: when
 * the VIVA partner credentials aren't set, start() returns { configured:false }.
 */
class VivaOnboardingService {
    start(locationId){
        let user = JSON.parse(localStorage.getItem('user'));
        let payload = { merchant: user.merchant, locationId: locationId };
        return axios.post('/api/site/viva/start', payload, { headers: authHeader() });
    }

    getStatus(locationId){
        let user = JSON.parse(localStorage.getItem('user'));
        let url = '/api/site/viva/status?merchant=' + user.merchant + '&locationId=' + locationId;
        return axios.get(url, { headers: authHeader() });
    }

    /**
     * Asks VIVA about the connected account instead of just re-reading our own doc, and completes the
     * onboarding if VIVA now reports the account verified. This is the only route that finishes onboarding
     * without a webhook - which is how it works in demo (VIVA runs no hosted flow there) and in dev (VIVA
     * cannot reach localhost).
     */
    refresh(locationId){
        let user = JSON.parse(localStorage.getItem('user'));
        let payload = { merchant: user.merchant, locationId: locationId };
        return axios.post('/api/site/viva/refresh', payload, { headers: authHeader() });
    }

    /**
     * SUPER_ADMIN only: «Bytt nettbetaling til VIVA» for a location whose onboarding is complete but waiting
     * (a merchant without SaaS features). Answers the status shape, or 400 with why not.
     */
    switchToViva(locationId){
        let user = JSON.parse(localStorage.getItem('user'));
        let payload = { merchant: user.merchant, locationId: locationId };
        return axios.post('/api/superadmin/viva/switchToViva', payload, { headers: authHeader() });
    }

    /**
     * SUPER_ADMIN only (/api/superadmin/**): what «Start VIVA-onboarding» would send to VIVA for this location,
     * built by the code that sends it, and the payment source that follows. Read-only.
     */
    getPreview(locationId){
        let user = JSON.parse(localStorage.getItem('user'));
        let url = '/api/superadmin/viva/onboardingPreview?merchant=' + user.merchant + '&locationId=' + locationId;
        return axios.get(url, { headers: authHeader() });
    }

    /**
     * SUPER_ADMIN only: what a location's tills and kiosks take cards with, and its VIVA setup for them
     * (Terminaler → «Kortbetaling i kasse og kiosk»). Never a credential.
     */
    getTillStatus(locationId){
        let user = JSON.parse(localStorage.getItem('user'));
        let url = '/api/superadmin/viva/tillStatus?merchant=' + user.merchant + '&locationId=' + locationId;
        return axios.get(url, { headers: authHeader() });
    }

    /** SUPER_ADMIN only: writes the location's VIVA setup for tills and kiosks from VIVA Partner Config. */
    tillSetUp(locationId){
        return this.tillAction('/api/superadmin/viva/tillSetUp', locationId);
    }

    /** SUPER_ADMIN only: the tills stop using Softpay and take VIVA; the Softpay setup is kept for going back. */
    tillSwitchToViva(locationId){
        return this.tillAction('/api/superadmin/viva/tillSwitchToViva', locationId);
    }

    /** SUPER_ADMIN only: puts the kept Softpay setup back, so the tills take Softpay again. */
    tillBackToSoftpay(locationId){
        return this.tillAction('/api/superadmin/viva/tillBackToSoftpay', locationId);
    }

    tillAction(url, locationId){
        let user = JSON.parse(localStorage.getItem('user'));
        return axios.post(url, { merchant: user.merchant, locationId: locationId }, { headers: authHeader() });
    }
}

export default new VivaOnboardingService();
